import { useAuth0 } from '@auth0/auth0-react';
import {
  AlertCircle,
  ArrowLeft,
  MessageSquare,
  Plus,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { useState, useEffect } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import ActionCardDetailsCard from '@/features/votes/ActionCardDetailsCard';
import { ChatTranscript } from '@/features/votes/chat';
import { entityTypeLabel } from '@/features/votes/entity-votes';
import MeetingAssistDetailsCard from '@/features/votes/MeetingAssistDetailsCard';
import type { AnnotationNote } from '@/features/votes/message-votes';
import {
  useEntityVoteDetails,
  useUpdateEntityAnnotation,
} from '@/features/votes/useEntityVotes';
import { useVoteTags, type VoteTagOption } from '@/features/votes/useVoteTags';

import type { MultiValue } from 'react-select';

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'] as const;

interface EntityVoteDetailsPageProps {
  voteId: number;
  isDiscussionOpen: boolean;
  onDiscussionOpenChange: (open: boolean) => void;
  onBack: () => void;
}

export default function EntityVoteDetailsPage({
  voteId,
  isDiscussionOpen,
  onDiscussionOpenChange,
  onBack,
}: EntityVoteDetailsPageProps) {
  const { data: voteDetails, isLoading, error } = useEntityVoteDetails(voteId);
  const updateAnnotation = useUpdateEntityAnnotation(voteId);
  const { data: tagOptions } = useVoteTags();
  const { user } = useAuth0();
  const [selectedTags, setSelectedTags] = useState<VoteTagOption[]>([]);
  const [status, setStatus] = useState<string>('');
  const [newNote, setNewNote] = useState<string>('');

  useEffect(() => {
    if (voteDetails?.annotation && tagOptions) {
      const annotation = voteDetails.annotation;
      setStatus(annotation.status || '');
      const tags = annotation.tags
        .map((tag) => tagOptions.find((option) => option.value === tag))
        .filter((option): option is VoteTagOption => option !== undefined);
      setSelectedTags(tags);
    }
  }, [voteDetails, tagOptions]);

  const updateAnnotationField = (updates: {
    status?: string | null;
    tags?: string[];
    notes?: AnnotationNote[];
  }) => {
    updateAnnotation.mutate({
      entity_type: 'entity_vote',
      entity_id: voteId,
      status: updates.status !== undefined ? updates.status : status || null,
      ...(updates.tags !== undefined ? { tags: updates.tags } : {}),
      ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
    });
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    updateAnnotationField({ status: newStatus || null });
  };

  const handleTagsChange = (options: MultiValue<VoteTagOption>) => {
    const newTags = [...options];
    setSelectedTags(newTags);
    updateAnnotationField({ tags: newTags.map((tag) => tag.value) });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const updatedNotes = [
      ...(voteDetails?.annotation?.notes || []),
      {
        note: newNote.trim(),
        user_email: user?.email || 'unknown',
        time: new Date().toISOString(),
      },
    ];
    setNewNote('');
    updateAnnotationField({ notes: updatedNotes });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Other Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-2 p-8">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="text-sm text-destructive font-medium">
              Error Loading Vote Details
            </p>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error
                ? error.message
                : 'Failed to load vote details'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Other Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!voteDetails) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Other Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">No vote details available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-full flex-col bg-background">
      {/* Header */}
      <div className="shrink-0 border-b px-3 py-4 sm:px-6">
        <div className="space-y-3">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Other Votes
          </Button>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">
                Entity Vote Details
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline">
                  {entityTypeLabel(voteDetails.entity_type)}
                </Badge>
                <div className="flex items-center gap-1">
                  {voteDetails.direction > 0 ? (
                    <ThumbsUp className="h-4 w-4 text-green-600" />
                  ) : (
                    <ThumbsDown className="h-4 w-4 text-red-600" />
                  )}
                  <Badge
                    variant="default"
                    className={
                      voteDetails.direction > 0
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-red-100 text-red-800 border-red-200'
                    }
                  >
                    {voteDetails.direction > 0 ? 'Upvote' : 'Downvote'}
                  </Badge>
                </div>
                <span className="font-mono text-xs text-muted-foreground">
                  Entity ID: {voteDetails.entity_id.slice(0, 8)}...
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="min-w-0 sm:min-w-[150px]">
                <Select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="h-9 text-sm"
                >
                  <option value="">Select status...</option>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="min-w-0 sm:min-w-[220px]">
                <MultiSelectFilter<VoteTagOption>
                  options={tagOptions ?? []}
                  value={selectedTags}
                  onChange={handleTagsChange}
                  placeholder="Select tags..."
                  classNamePrefix="annotation-tags"
                />
              </div>

              <Button
                onClick={() => onDiscussionOpenChange(true)}
                variant="outline"
                size="sm"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                View Discussion
              </Button>
            </div>
          </div>

          {updateAnnotation.isError && (
            <p className="text-sm text-destructive">
              {updateAnnotation.error instanceof Error
                ? updateAnnotation.error.message
                : 'Failed to save annotation'}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="mx-auto max-w-5xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6">
            {/* Reasoning Card */}
            {voteDetails.reasoning && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">User Reasoning</CardTitle>
                  <CardDescription>Why the user voted this way</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">
                    {voteDetails.reasoning}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Meeting Details Card */}
            {voteDetails.meeting && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Meeting Details</CardTitle>
                  <CardDescription>
                    {voteDetails.meeting.name || 'Unnamed Meeting'}
                    <span className="ml-2 text-xs font-mono">
                      ({voteDetails.meeting.meeting_id.slice(0, 8)}...)
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>
                      Status:{' '}
                      <Badge variant="outline">
                        {voteDetails.meeting.status}
                      </Badge>
                    </span>
                    <span>
                      Created: {formatDate(voteDetails.meeting.created_at)}
                    </span>
                  </div>

                  {voteDetails.meeting.tldr && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">TL;DR</h4>
                      <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap">
                        {voteDetails.meeting.tldr}
                      </div>
                    </div>
                  )}

                  {voteDetails.meeting.summary && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Summary</h4>
                      <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap">
                        {voteDetails.meeting.summary}
                      </div>
                    </div>
                  )}

                  {voteDetails.meeting.notes && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Notes</h4>
                      <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap max-h-96 overflow-auto">
                        {voteDetails.meeting.notes}
                      </div>
                    </div>
                  )}

                  {voteDetails.meeting.personified_transcript && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">
                        Personified Transcript
                      </h4>
                      <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap max-h-96 overflow-auto">
                        {voteDetails.meeting.personified_transcript}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Meeting Prep Card */}
            {voteDetails.meeting_prep && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Meeting Prep</CardTitle>
                  <CardDescription>
                    {voteDetails.meeting_prep.meeting_name || 'Unnamed Meeting'}
                    <span className="ml-2 text-xs font-mono">
                      ({voteDetails.meeting_prep.meeting_id.slice(0, 8)}...)
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>
                      Agent:{' '}
                      <Badge variant="outline">
                        {voteDetails.meeting_prep.agent_name}
                      </Badge>
                    </span>
                    <span>
                      Created: {formatDate(voteDetails.meeting_prep.created_at)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Prep Content</h4>
                    <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap max-h-96 overflow-auto">
                      {voteDetails.meeting_prep.content}
                    </div>
                  </div>

                  {voteDetails.meeting_prep.chat_history ? (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Generation Chat</h4>
                      <div className="rounded-md border p-4">
                        <ChatTranscript
                          chatHistory={voteDetails.meeting_prep.chat_history}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Generation chat not available for this prep
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {voteDetails.meeting_assist && (
              <MeetingAssistDetailsCard details={voteDetails.meeting_assist} />
            )}

            {voteDetails.action_card && (
              <ActionCardDetailsCard card={voteDetails.action_card} />
            )}

            {/* Routine Report Card */}
            {voteDetails.routine_report && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Routine Report</CardTitle>
                  <CardDescription>
                    {voteDetails.routine_report.title || 'Untitled Report'}
                    <span className="ml-2 text-xs font-mono">
                      ({voteDetails.routine_report.report_id.slice(0, 8)}...)
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {voteDetails.routine_report.template_title && (
                      <span>
                        Template:{' '}
                        <Badge variant="outline">
                          {voteDetails.routine_report.template_title}
                        </Badge>
                      </span>
                    )}
                    <span>
                      Routine ID:{' '}
                      <Badge variant="outline" className="font-mono">
                        {voteDetails.routine_report.routine_id}
                      </Badge>
                    </span>
                    {voteDetails.routine_report.experiment_slug && (
                      <span>
                        Experiment:{' '}
                        <Badge variant="outline">
                          {voteDetails.routine_report.experiment_slug}
                        </Badge>
                        {voteDetails.routine_report.rollout_slug && (
                          <span className="ml-1 text-xs">
                            ({voteDetails.routine_report.rollout_slug})
                          </span>
                        )}
                      </span>
                    )}
                    {voteDetails.routine_report.prompt_version && (
                      <span>
                        Prompt:{' '}
                        <Badge variant="outline" className="font-mono">
                          {voteDetails.routine_report.prompt_version}
                        </Badge>
                      </span>
                    )}
                    <span>
                      Date: {formatDate(voteDetails.routine_report.date)}
                    </span>
                    <span>
                      Created:{' '}
                      {formatDate(voteDetails.routine_report.created_at)}
                    </span>
                  </div>

                  {voteDetails.routine_report.text && (
                    <div className="p-3 rounded-md bg-muted/50 text-sm whitespace-pre-wrap max-h-96 overflow-auto">
                      {voteDetails.routine_report.text}
                    </div>
                  )}

                  {voteDetails.routine_report.prompt && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-muted-foreground">
                        Prompt it ran with
                      </summary>
                      <div className="mt-2 p-3 rounded-md bg-muted/50 whitespace-pre-wrap max-h-96 overflow-auto">
                        {voteDetails.routine_report.prompt}
                      </div>
                    </details>
                  )}
                </CardContent>
              </Card>
            )}

            {/* No entity data fallback */}
            {!voteDetails.meeting && voteDetails.entity_type === 'meeting' && (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">
                    Meeting data not available
                  </p>
                </CardContent>
              </Card>
            )}

            {!voteDetails.meeting_prep &&
              voteDetails.entity_type === 'meeting_prep' && (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">
                      Meeting prep data not available
                    </p>
                  </CardContent>
                </Card>
              )}

            {!voteDetails.meeting_assist &&
              voteDetails.entity_type === 'meeting_assist' && (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">
                      Meeting Assist data not available
                    </p>
                  </CardContent>
                </Card>
              )}

            {!voteDetails.action_card &&
              voteDetails.entity_type === 'action_card' && (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">
                      Action card data not available
                    </p>
                  </CardContent>
                </Card>
              )}

            {!voteDetails.routine_report &&
              (voteDetails.entity_type === 'routine_report' ||
                voteDetails.entity_type === 'im_channel_proactive_routine') && (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">
                      Routine report data not available
                    </p>
                  </CardContent>
                </Card>
              )}
          </div>
        </ScrollArea>
      </div>

      <Sheet open={isDiscussionOpen} onOpenChange={onDiscussionOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Discussion Notes</SheetTitle>
            <SheetDescription>
              View and add notes for this entity vote
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {voteDetails?.annotation?.notes &&
            voteDetails.annotation.notes.length > 0 ? (
              <ScrollArea className="h-[calc(100vh-350px)]">
                <div className="space-y-3 pr-4">
                  {voteDetails.annotation.notes.map((note, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-md border bg-muted/50 space-y-1"
                    >
                      <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{note.user_email}</span>
                        <span>•</span>
                        <span>
                          {new Date(note.time).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No notes yet. Add one below.
              </p>
            )}

            <div className="space-y-3 pt-4 border-t">
              <Textarea
                placeholder="Write your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={4}
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim() || updateAnnotation.isPending}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {updateAnnotation.isPending ? 'Adding Note...' : 'Add Note'}
              </Button>
              {updateAnnotation.isError && (
                <p className="text-sm text-destructive text-center">
                  {updateAnnotation.error instanceof Error
                    ? updateAnnotation.error.message
                    : 'Failed to add note'}
                </p>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
